import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import PageHeader from "../../components/PageHeader";
import SectionLabel from "../../components/SectionLabel";
import API_URL from "../../config";
import { useAuth } from "../../context/AuthContext";

const SpectacleCreate = () => {
  const { token, role } = useAuth();
  const isAdmin = role === "ROLE_ADMIN";
  const [artists, setArtists] = useState([]);
  const [producteurs, setProducteurs] = useState([]);

  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [formData, setFormData] = useState({
    title: "",
    description: "",
    date: "",
    location: "",
    price: "",
    artistId: "",
    coArtistIds: [],
    producerId: "",
    coProducerIds: [],
  });

  const handleChange = (e) =>
    setFormData({ ...formData, [e.target.id]: e.target.value });

  const handleMultiChange = (key) => (e) => {
    const values = Array.from(e.target.selectedOptions, (o) => o.value);
    setFormData({ ...formData, [key]: values });
  };

  const handleSubmit = () => {
    if (
      !formData.title ||
      !formData.date ||
      !formData.price ||
      !formData.artistId
    ) {
      setError("Titre, date, prix et artiste principal sont obligatoires.");
      return;
    }
    setLoading(true);
    setError(null);

    const primaryArtistId = parseInt(formData.artistId);
    const coArtists = formData.coArtistIds
      .map((id) => parseInt(id))
      .filter((id) => !isNaN(id) && id !== primaryArtistId)
      .map((id) => ({ id }));

    const body = {
      title: formData.title,
      description: formData.description,
      date: new Date(formData.date).toISOString(),
      location: formData.location,
      price: parseFloat(formData.price),
      artist: { id: primaryArtistId },
      coArtists,
    };

    if (isAdmin) {
      const primaryProducerId = formData.producerId
        ? parseInt(formData.producerId)
        : null;
      if (primaryProducerId) {
        body.producer = { id: primaryProducerId };
      }
      body.coProducers = formData.coProducerIds
        .map((id) => parseInt(id))
        .filter((id) => !isNaN(id) && id !== primaryProducerId)
        .map((id) => ({ id }));
    }

    fetch(`${API_URL}/api/spectacles`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(body),
    })
      .then((res) => {
        if (!res.ok)
          return res.text().then((t) => {
            throw new Error(t);
          });
        return res.json();
      })
      .then(() => navigate("/"))
      .catch((err) => {
        setError(err.message);
        setLoading(false);
      });
  };

  useEffect(() => {
    fetch(`${API_URL}/api/artists`)
      .then((res) => res.json())
      .then((data) => setArtists(Array.isArray(data) ? data : []));

    if (isAdmin) {
      fetch(`${API_URL}/api/users`, {
        headers: { Authorization: `Bearer ${token}` },
      })
        .then((res) => res.json())
        .then((data) => {
          const list = Array.isArray(data) ? data : [];
          setProducteurs(list.filter((u) => u.role === "ROLE_PRODUCTEUR"));
        })
        .catch(() => setProducteurs([]));
    }
  }, [isAdmin, token]);

  const coArtistOptions = artists.filter(
    (a) => String(a.id) !== String(formData.artistId),
  );
  const coProducerOptions = producteurs.filter(
    (p) => String(p.id) !== String(formData.producerId),
  );

  return (
    <>
      <PageHeader
        title="Nouveau spectacle"
        subtitle="Remplissez le formulaire ci-dessous."
        breadcrumb={[
          { label: "Spectacles", path: "/" },
          { label: "Nouveau spectacle" },
        ]}
      />

      <section className="py-5 bg-light">
        <div className="container">
          <div className="row justify-content-center">
            <div className="col-lg-8">
              {error && (
                <div className="alert-error">
                  <i className="fas fa-exclamation-circle me-2"></i>
                  {error}
                </div>
              )}

              <div className="agency-card">
                <div className="card-accent"></div>
                <div className="card-content">
                  <SectionLabel
                    icon="theater-masks"
                    text="Informations principales"
                  />

                  <div className="row g-3 mb-3">
                    <div className="col-md-6">
                      <label className="agency-label">Titre *</label>
                      <input
                        type="text"
                        id="title"
                        className="form-control"
                        placeholder="Ex: Le Lac des Cygnes"
                        value={formData.title}
                        onChange={handleChange}
                      />
                    </div>
                    <div className="col-md-6">
                      <label className="agency-label">Lieu</label>
                      <input
                        type="text"
                        id="location"
                        className="form-control"
                        placeholder="Ex: Opéra de Paris"
                        value={formData.location}
                        onChange={handleChange}
                      />
                    </div>
                  </div>

                  <div className="row g-3">
                    <div className="col-md-6">
                      <label className="agency-label">Date *</label>
                      <input
                        type="datetime-local"
                        id="date"
                        className="form-control"
                        value={formData.date}
                        onChange={handleChange}
                      />
                    </div>
                    <div className="col-md-6">
                      <label className="agency-label">Prix (€) *</label>
                      <input
                        type="number"
                        id="price"
                        className="form-control"
                        placeholder="Ex: 25.00"
                        min="0"
                        step="0.01"
                        value={formData.price}
                        onChange={handleChange}
                      />
                    </div>
                  </div>

                  <hr className="agency-divider" />
                  <SectionLabel icon="music" text="Artistes" />

                  <div className="row g-3">
                    <div className="col-md-6">
                      <label className="agency-label">Artiste principal *</label>
                      <select
                        id="artistId"
                        className="form-control"
                        value={formData.artistId}
                        onChange={handleChange}
                      >
                        <option value="">Sélectionner un artiste</option>
                        {artists.map((artist) => (
                          <option key={artist.id} value={artist.id}>
                            {artist.name}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="col-md-6">
                      <label className="agency-label">
                        Autres artistes (Ctrl/Cmd + clic)
                      </label>
                      <select
                        multiple
                        className="form-control"
                        value={formData.coArtistIds}
                        onChange={handleMultiChange("coArtistIds")}
                        size={Math.min(6, Math.max(3, coArtistOptions.length))}
                      >
                        {coArtistOptions.map((artist) => (
                          <option key={artist.id} value={artist.id}>
                            {artist.name}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>

                  {isAdmin && (
                    <>
                      <hr className="agency-divider" />
                      <SectionLabel icon="briefcase" text="Producteurs (admin)" />

                      <div className="row g-3">
                        <div className="col-md-6">
                          <label className="agency-label">
                            Producteur principal
                          </label>
                          <select
                            id="producerId"
                            className="form-control"
                            value={formData.producerId}
                            onChange={handleChange}
                          >
                            <option value="">Aucun</option>
                            {producteurs.map((p) => (
                              <option key={p.id} value={p.id}>
                                {p.firstName} {p.lastName} ({p.email})
                              </option>
                            ))}
                          </select>
                        </div>
                        <div className="col-md-6">
                          <label className="agency-label">
                            Autres producteurs (Ctrl/Cmd + clic)
                          </label>
                          <select
                            multiple
                            className="form-control"
                            value={formData.coProducerIds}
                            onChange={handleMultiChange("coProducerIds")}
                            size={Math.min(
                              6,
                              Math.max(3, coProducerOptions.length || 3),
                            )}
                          >
                            {coProducerOptions.map((p) => (
                              <option key={p.id} value={p.id}>
                                {p.firstName} {p.lastName} ({p.email})
                              </option>
                            ))}
                          </select>
                        </div>
                      </div>
                    </>
                  )}

                  <hr className="agency-divider" />
                  <SectionLabel icon="file-alt" text="Description" />

                  <textarea
                    id="description"
                    className="form-control"
                    rows="5"
                    maxLength="1000"
                    placeholder="Décrivez le spectacle..."
                    value={formData.description}
                    onChange={handleChange}
                  />
                  <div className="char-count">
                    {formData.description.length} / 1000
                  </div>

                  <hr className="agency-divider" />
                  <div className="d-flex justify-content-between align-items-center">
                    <Link to="/" className="btn-cancel">
                      <i className="fas fa-arrow-left me-2"></i>Annuler
                    </Link>
                    <button
                      onClick={handleSubmit}
                      disabled={loading}
                      className="btn btn-primary btn-xl text-uppercase btn-admin"
                    >
                      {loading ? (
                        <>
                          <span className="spinner-border spinner-border-sm me-2"></span>
                          Création...
                        </>
                      ) : (
                        <>
                          <i className="fas fa-plus me-2"></i>Créer le spectacle
                        </>
                      )}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default SpectacleCreate;
